import { Component, OnInit, Renderer2, ElementRef, ViewChild, HostListener } from '@angular/core';
import { getNavData } from './_nav';

import { AuthService } from '../services/auth.service';
import { SocketService } from './services/socket.service';
import { NotificationFeedService, InboxItem } from './services/notification-feed.service';

import { apiUrl } from '../../environments/environment';
import { IStepOption, TourService } from 'ngx-ui-tour-md-menu';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { ThemeService } from './services/theme.service';
import { FcmService } from '../services/fcm.service';
import { MenuService } from './services/menu.service';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit {

  @ViewChild('navContainer', { static: false }) navContainer!: ElementRef;

  scrollDirection: 'left' | 'right' = 'right'; // default direction if needed

  // ── Top-menu horizontal pager (MakeMyTrip-style page slide) ──────────────────
  navOffset = 0;            // current translateX offset of the nav strip (px, >= 0)
  navAtStart = true;        // left arrow disabled when true
  navAtEnd = true;          // right arrow disabled when true
  navHasOverflow = false;   // false → everything fits, both arrows greyed

  public currentYear = new Date();
  public sidebarMinimized = false;
  public navItems:any[] = [];
  public headerItems:any[] = [];
  public avatarItems:any[] = [];   // A2.1 — server-driven avatar dropdown (flag-gated)

  name = '';
  menu: any;
  // A2.1/A4.2 — flag-gated server-driven menu. Legacy global_menu path unchanged
  // unless this resolves true. Precedence (highest wins):
  //   1. URL ?menuEngine=1/0  — QA/support per-session force on/off
  //   2. server flag USER_DATA.menu_engine_web — the per-org A4.2 rollout switch
  // The server is now AUTHORITATIVE: a normal user's menu follows the org's
  // rollout row, so a rollback (web_enabled=0) actually takes effect. The old
  // sticky `localStorage USE_MENU_ENGINE` opt-in is intentionally NOT consulted
  // (stale June-test values would otherwise defeat server rollback).
  useMenuEngine = this.resolveMenuEngineFlag();

  // The org's own head user (user_role 6) has a FIXED, static nav — Programs +
  // User Management, see getOrgAdminNavData() in _nav.ts — that must never be
  // replaced by whatever menu has been assigned to the org through menu
  // management. Everything below that could swap the menu (the per-org web
  // rollout flag, the ?menuEngine= URL override, the cached sidebar restore in
  // ngOnInit) therefore has to stand down for this role.
  private isOrgHead(): boolean {
    try {
      const raw = localStorage.getItem('USER_DATA');
      const u = raw ? JSON.parse(raw) : null;
      return !!(u && u.user_role == 6);
    } catch (e) { return false; }
  }

  // True for a node that belongs to the static org-head nav (menu_ids all start
  // 'orgadmin-'). Used to throw away a sidebar/header cached in localStorage by
  // an earlier engine-driven session instead of restoring it over the static nav.
  private isStaticOrgHeadNode(n: any): boolean {
    return !!n && String(n.menu_id || '').indexOf('orgadmin-') === 0;
  }

  private resolveMenuEngineFlag(): boolean {
    if (this.isOrgHead()) return false;
    try {
      // 1) URL override (session) — cover path (?menuEngine=1) and hash (#/user/...?menuEngine=1) routing.
      const search = window.location.search || '';
      const hash = window.location.hash || '';
      const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?')) : '';
      const qp = new URLSearchParams(search).get('menuEngine')
              ?? new URLSearchParams(hashQuery).get('menuEngine');
      if (qp === '1' || qp === 'true') return true;    // force-on this session
      if (qp === '0' || qp === 'false') return false;  // force-off this session

      // 2) server per-org rollout flag from the login payload (the real driver).
      const raw = localStorage.getItem('USER_DATA');
      if (raw) {
        const u = JSON.parse(raw);
        return !!(u && u.menu_engine_web === true);
      }
    } catch (e) { /* SSR / no window / bad JSON → legacy menu */ }
    return false;
  }
  sideMenu = [];
  currentHead = {};
  userDetails:any;
  apiLink = apiUrl;
  headerLogo = 'assets/img/brand/verdentumlogo_green.png';
  footerLogo = '';
  orgDetails = {
    pm_logo: null,
    org_code: null
  };

  response:any;
  pushMessage: any;

  themes = [
    { name: 'Default Theme', color: '#2e2c34', id: 'default' },
    { name: 'Midnight Blue Theme', color: '#0f042b', id: 'midnight-blue' },
    { name: 'Black Coffee Theme', color: '#1c0700', id: 'black-coffee' },
    { name: 'Black Red Theme', color: '#190000', id: 'black-red' },
    { name: 'Midnight Green Theme', color: '#001721', id: 'midnight-green' },
    { name: 'Dark Maroon Theme', color: '#26000e', id: 'dark-maroon' },
    { name: 'Deep Navy Theme', color: '#00152c', id: 'deep-navy' },
    { name: 'Dark Forest Green Theme', color: '#132001', id: 'dark-forest-green' },
    { name: 'Deep Maroon Theme', color: '#270007', id: 'deep-maroon' },
    { name: 'Deep Brownish Red Theme', color: '#200800', id: 'deep-brownish-red' },
    { name: 'Dark Coffee Theme', color: '#1f1500', id: 'dark-coffee' },
    { name: 'Dark Olive Theme', color: '#121500', id: 'dark-olive' },
    { name: 'Dark Cerulean Theme', color: '#002334', id: 'dark-cerulean' },
  ];
  currentTheme = '';
  zoomFactor = 1;

  // ── Notification Centre (header bell) ─────────────────────────────────────
  bellOpen = false;
  bellLoading = false;
  notifItems: InboxItem[] = [];
  unreadCount = 0;
  notifFilter: 'all' | 'unread' = 'all';
  notifLoadingMore = false;
  notifCanLoadMore = false;
  private readonly notifPageSize = 20;
  private notifPoll: any = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private renderer: Renderer2,
    private toastr: ToastrService,
    public authService: AuthService,
    public socketSercie: SocketService,
    public themeService: ThemeService,
    private fcmService: FcmService,
    private readonly tourService: TourService,
    public notifFeed: NotificationFeedService,
    private menuService: MenuService,
  ) {
    let user:any = localStorage.getItem('USER_DATA');
    this.menu = JSON.parse(user).menu;

    const navData = getNavData();
    this.navItems = navData.navItems;
    this.headerItems = navData.headerItems;

    if(this.headerItems.length === 0) {
      this.getHeaderMenu();
    }

    this.orgDetails = JSON.parse(user).organization;
    if(this.orgDetails.pm_logo && this.orgDetails.pm_logo != 'default_user.png') {
      this.headerLogo = this.apiLink + '/api/lookup/s3-signed-url?key=' + this.orgDetails.org_code + '/orgsetup/' + this.orgDetails.pm_logo;
      this.footerLogo = 'assets/img/brand/verdentumlogo_green.png';
    } else {
      this.footerLogo = '';
      this.headerLogo = 'assets/img/brand/verdentumlogo_green.png';
    }

    this.authService.urlChangeObservable.subscribe((res:any) => {
      if(res) {
        // Clear left side
        if(res == 'clearLeftSide') {
          localStorage.removeItem('currentSides');
          localStorage.removeItem('selectedSide');
          this.navItems = [];
        }

        // Clear current head menu
        if(res == 'currentHead') {
          localStorage.removeItem('currentHead');
          this.currentHead = {};
        }
      }
    });

    this.fcmService.receiveMessage();
    this.fcmService.currentMessage.subscribe((message) => {
      this.pushMessage = message;
    });
  }

  async ngOnInit() {
    await this.getUserDetails();

    // A2.1 — server-driven menu (flag-gated). On failure, silently keep legacy menu.
    if (this.useMenuEngine) {
      try {
        await this.loadServerMenu();
      } catch (e) {
        console.warn('[menu-engine] load failed, falling back to legacy menu', e);
      }
    }

    // ── Realtime: connect to /rt so the bell + pages update live ────────────
    this.socketSercie.connect();

    // A3.1 — live menu refresh (no logout) when an admin changes the menu engine
    if (this.useMenuEngine) {
      this.socketSercie.on('menu.updated').subscribe(() => {
        this.loadServerMenu();
      });
    }

    // ── Notification bell: live unread badge ────────────────────────────────
    // The socket (via NotificationFeedService) refreshes the count instantly;
    // the slow poll is a safety-net fallback if the socket is down.
    this.notifFeed.unread$.subscribe(n => this.unreadCount = n);
    this.notifFeed.refreshUnread();
    this.notifPoll = setInterval(() => this.notifFeed.refreshUnread(), 120000);

    // Restore header/side menu from localStorage — but NOT when the menu engine is on:
    // loadServerMenu() already derived currentHead + navItems from the server tree, and
    // the cached values are legacy-shaped, so restoring them would clobber the server menu.
    if (!this.useMenuEngine) {
      // Check if header menu is set in localStorage
      let currentHead = localStorage.getItem('currentHead');
      if(currentHead && currentHead.length > 0) {
        const cachedHead = JSON.parse(currentHead);
        // Org head: ignore a header cached from an earlier engine-driven session.
        if (!this.isOrgHead() || this.isStaticOrgHeadNode(cachedHead)) this.currentHead = cachedHead;
      }

      // Check if side menu is set in localStorage
      let currentSides = localStorage.getItem('currentSides');
      if(currentSides && currentSides.length > 0) {
        const cachedSides = JSON.parse(currentSides);
        // Org head: same — only restore a sidebar that is part of the static nav.
        if (!this.isOrgHead() || (Array.isArray(cachedSides) && cachedSides.every((n: any) => this.isStaticOrgHeadNode(n)))) {
          this.navItems = cachedSides;
        }
      }
    }

    if(localStorage.getItem('selectedSide')) {
      let link = localStorage.getItem('selectedSide');
      // if(link.length > 0) this.router.navigate([link]);
    }

    // Check if first time login of user
    // Then start tour
    if(this.userDetails.user.profile_filled != 1) {
      this.setupTour();
    }
    
    if(this.authService.isAuthenticated()) this.chnekUsertheme();
  }
  ngAfterViewInit() {
    this.initNav();
  }

  // Re-baseline the top-menu pager: reset scroll to the start and recompute which
  // arrows should be enabled. MUST run after the header *ngFor has rendered — so it's
  // also re-invoked when the server menu replaces headerItems asynchronously.
  initNav() {
    if (!this.navContainer) return;
    this.navOffset = 0;
    this.navContainer.nativeElement.style.transform = 'translateX(0px)';
    this.updateNavArrows();
  }

  // Recompute overflow + arrow-disabled state from current geometry.
  updateNavArrows() {
    if (!this.navContainer) return;
    const ul = this.navContainer.nativeElement;
    const viewport = ul.parentElement ? ul.parentElement.clientWidth : ul.clientWidth;
    const maxOffset = Math.max(0, ul.scrollWidth - viewport);
    // clamp current offset in case the viewport shrank/grew
    this.navOffset = Math.min(this.navOffset, maxOffset);
    ul.style.transform = `translateX(-${this.navOffset}px)`;
    this.navHasOverflow = maxOffset > 1;
    this.navAtStart = this.navOffset <= 0;
    this.navAtEnd = this.navOffset >= maxOffset - 1;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateNavArrows();
  }

  /**
   * What each top-level section is FOR, keyed by the menu-engine item key.
   *
   * The generated fallback below can only ever list page names back at the user
   * ("this is where you will find View Programs, Create Program"), which tells
   * somebody who has never seen the product nothing they could not read off the
   * menu itself. These say what the section is actually for, in the order the
   * work happens: plan it, do it, see it.
   *
   * Keyed on `item_key` so it survives an org renaming its menu labels — the
   * label is theirs to change, the key is not. A section with no entry falls
   * back to the generated line, so an org's custom module still gets a card.
   */
  /**
   * The sections the first-run tour actually walks, in the order the work
   * happens: plan it, do it, see it, then run the organisation.
   *
   * The tour used to step through EVERY top-level header — 29 cards on a
   * greenroots admin — which is an index, not an introduction, and the only
   * way out was a grey \u2715 in a corner. A new user needs to know the shape of
   * the product, not to be walked past every module they will not open today.
   *
   * Anything not listed still appears in the menu; it just does not get a card.
   * Sections are shown only if the user's own menu has them, so a role that
   * cannot see Payroll is never told about it.
   */
  private static readonly TOUR_ORDER: string[] = [
    'grp.ppm',              // plan  — where programmes and the log-frame live
    'activity.execution',   // do    — the field's main entry point
    'grp.data-collection',  // do    — forms and responses
    'grp.task-mgmt',        // do    — tasks + the timesheet payroll reads
    'grp.employee',         // do    — people and pay
    'worldview.dashboard',  // see   — the widest view
    'reports.home',         // see   — what you send out
    'grp.user-mgmt',        // run   — who gets to see what
    'grp.notifications',    // run   — approvals waiting on you
  ];

  /** How many cards a first run is allowed to be. */
  private static readonly TOUR_MAX = 8;

  private static readonly TOUR_BLURBS: { [key: string]: string } = {
    // -- plan -----------------------------------------------------------------
    'grp.ppm':
      'Programmes and projects live here, together with the log-frame that ties activities to ' +
      'outputs, outcomes and impact. Targets are set period by period; everything else on the ' +
      'platform measures against them.',
    'grp.pip':
      'The implementation plan behind a project \u2014 what happens, in what order, and by when.',
    'grp.planning':
      'Budget and schedule set against the results you committed to, so a plan and its funding ' +
      'stay in the same place.',
    'proposal.home':
      'Draft and keep proposals, so what was promised to a funder is next to what is being ' +
      'delivered.',
    'grp.measurement':
      'The measurement model \u2014 how a result is defined and counted before any number is ' +
      'entered against it.',
    'grp.sector':
      'Ready-made sector content you can start from instead of building a log-frame from a blank ' +
      'page.',
    'sectorspecific.home':
      'Screens and fields specific to your sector, on top of the standard programme model.',

    // -- do -------------------------------------------------------------------
    'activity.execution':
      'Record an activity as it actually happened \u2014 participants, location, date and evidence. ' +
      'This is the entry point most field work flows through.',
    'grp.task-mgmt':
      'Day-to-day work: tasks, projects and your timesheet. Hours logged here are the same hours ' +
      'payroll and the monthly workforce report read.',
    'grp.employee':
      'People and pay in one place \u2014 your own timesheet, leave and payslips, your team\'s ' +
      'attendance and requests, and for admins the payroll run itself.',
    'grp.emp-attendance':
      'Attendance: who was in, on which day, and how that lands on the month.',
    'feed.home':
      'What the field is posting right now \u2014 updates, photos and data as they arrive.',
    'post.field-updates':
      'What the field is posting right now \u2014 updates, photos and data as they arrive.',
    'filemanager.home':
      'Documents and evidence kept with the programme they belong to, rather than in somebody\'s ' +
      'inbox.',
    'approveddata.home':
      'Data waiting to be checked before it counts towards a target. Nothing sitting here is in ' +
      'your numbers yet.',
    'grp.notifications':
      'Approvals waiting on you, and alerts when something needs attention.',
    'approval.workflows':
      'Who signs off on what, and in what order. Flows are versioned, so an old approval still ' +
      'shows the rule it was approved under.',
    'grp.procurement':
      'Requirements, quotations and orders \u2014 from raising a need to receiving it.',

    // -- see ------------------------------------------------------------------
    'worldview.dashboard':
      'Everything you are scoped to, on one map and one set of charts \u2014 the widest view of the ' +
      'programme the platform can give you.',
    'dashboards.home':
      'Charts built on your live data. Which ones appear depends on the dashboards configured for ' +
      'your organisation.',
    'shareable.view':
      'A dashboard you can share outside the platform when a funder or partner needs the numbers ' +
      'without an account.',
    'reports.home':
      'Standard reports across programmes, projects and indicators, ready to export.',
    'report.me-progress':
      'Progress against plan \u2014 what was targeted for the period, what was achieved, and the gap.',
    'reportbuilder.home':
      'Build a report to your own shape when the standard ones do not fit, and save it to run ' +
      'again next period.',
    'grp.impact-mgmt':
      'Impact: what changed for the people the programme is for, not just what was delivered.',
    'grp.impact-research':
      'The research behind the impact claim \u2014 studies, evidence and their findings.',
    'grp.impact-risk':
      'What could stop the programme working, and what is being done about it.',
    'grp.rca':
      'When a number misses, work back to why \u2014 cause by cause, rather than by argument.',

    // -- run the organisation --------------------------------------------------
    'grp.user-mgmt':
      'Who is in the organisation, what role they hold, and which projects and locations they can ' +
      'reach. Roles decide what each person sees.',
    'grp.domain-registry':
      'The shared lists the whole organisation draws on \u2014 sectors, units, categories. Change ' +
      'one here and every screen that uses it follows.',
    'category.home':
      'Categories used to group and filter your data across the platform.',
    'grp.crm':
      'Donors, partners and the conversations with them.',
    'grp.grant':
      'Grants, donors and partners \u2014 what each one funds and what it expects back.',
    'grp.demo-mgmt':
      'Demo content for showing the platform without touching your live programme data.',
  };

  /**
   * The line of copy inside one tour card.
   *
   * Order of preference: the menu row's own blurb (legacy menus carry one), then
   * the curated line above, then a line generated from the section's own pages.
   *
   * The generated fallback exists because menu-engine nodes have no `content` at
   * all (convertServerNode sets it to ''), so on a server-driven menu every card
   * came out with a title, two buttons and an empty body — a walkthrough that
   * walks you around without saying anything.
   */
  private tourBlurb(item:any): string {
    const own = (item && item.content ? String(item.content) : '').trim();
    if (own) return own;

    const curated = item && item.key ? UserComponent.TOUR_BLURBS[item.key] : null;
    if (curated) return curated;

    const names = (item.children || [])
      .reduce((acc:string[], c:any) => {
        if (c.children && c.children.length) {
          c.children.forEach((g:any) => { if (g.name) acc.push(g.name); });
        } else if (c.name) acc.push(c.name);
        return acc;
      }, [])
      .slice(0, 3);

    if (!names.length) return `Open ${item.name} from here.`;
    const list = names.length > 1
      ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
      : names[0];
    return `${item.name} — this is where you will find ${list}.`;
  }

  async setupTour() {
    let tourItems:any[] = [];
    for(const itm of this.headerItems) {
      let item:any = itm;

      // Set tour link if item is post
      if(item.url == 'user/post') {
        tourItems.push({
          anchorId: item.menu_id.toString(),
          key: 'post.field-updates',
          content: 'Get real-time updates on what is happening on the ground for your projects. Scroll through the data and image uploads by the team as if you are using a social media platform.',
          title: item.name,
          route: item.url
        });
        continue;
      }

      // §M7 — the tour NAVIGATES to each step's route (ngx-ui-tour-core calls
      // navigateByUrl), so every route here has to be a real page.
      //
      // This used to take `item.children[0].url` — the first child, whatever it
      // was. Under the menu engine a group carries a deliberately non-routable
      // placeholder (`menu-group/<key>`, see convertServerNode), and the first
      // child of a top-level header is very often another group. So the tour
      // navigated to `user/menu-group/grp.ppm`, no route matched, and the
      // catch-all dropped the user on /user/404 — on their FIRST EVER login,
      // which is the only time the tour runs (profile_filled != 1).
      //
      // firstLeafUrl() already solves exactly this for the header-click path and
      // its own comment says "navigating to that 404s". Use it here too, and drop
      // any header that has no reachable page rather than pointing a tour step at
      // something that cannot open.
      const leaf = (item.children && item.children.length)
        ? this.firstLeafUrl(item.children)
        : (item.url && String(item.url).indexOf('menu-group/') !== 0 ? item.url : null);

      if (!leaf) continue;

      tourItems.push({
        anchorId: item.menu_id.toString(),
        key: item.key,
        content: this.tourBlurb(item),
        title: item.name,
        route: 'user/' + leaf
      });
    }

    // Shortlist + order. TOUR_ORDER decides both which sections are worth a card
    // and what sequence they are shown in — menu order is an org's arrangement
    // decision, which is not the same thing as a sensible way to learn the
    // product. Anything not on the list is kept behind the listed ones and only
    // used to top up if this menu is a small one.
    const rank = (t:any) => {
      const i = UserComponent.TOUR_ORDER.indexOf(t.key);
      return i === -1 ? 999 : i;
    };
    tourItems = tourItems
      .filter((t:any) => rank(t) !== 999)
      .sort((a:any, b:any) => rank(a) - rank(b))
      .slice(0, UserComponent.TOUR_MAX);

    // A menu with none of the listed sections (a narrow role, or an org whose
    // menu is all custom items) would otherwise get no tour at all. Fall back to
    // that menu's own first few headers rather than showing nothing.
    if (!tourItems.length) {
      tourItems = this.headerItems
        .filter((h:any) => h.children && h.children.length ? this.firstLeafUrl(h.children) : (h.url && String(h.url).indexOf('menu-group/') !== 0))
        .slice(0, 4)
        .map((h:any) => ({
          anchorId: h.menu_id.toString(),
          key: h.key,
          content: this.tourBlurb(h),
          title: h.name,
          route: 'user/' + ((h.children && h.children.length) ? this.firstLeafUrl(h.children) : h.url)
        }));
    }

    // Number the cards and label the buttons. "Step 3 of 8" is the difference
    // between a walkthrough somebody finishes and one they dismiss; `content` is
    // bound with [innerHTML] by the library, so the counter can live in it
    // without touching the library's template.
    const total = tourItems.length;
    tourItems = tourItems.map((t:any, i:number) => ({
      anchorId: t.anchorId,
      title: t.title,
      route: t.route,
      content: `<span class="tour-count">Step ${i + 1} of ${total}</span>` + t.content,
      prevBtnTitle: 'Back',
      nextBtnTitle: 'Next',
      endBtnTitle: 'Start using Verdentum'
    }));

    // Nothing navigable at all (a menu of empty groups): skip the tour rather
    // than start one that cannot move. The user still gets marked as onboarded
    // below so they are not shown a broken tour on every login.
    if (!tourItems.length) {
      this.userDetails.user.profile_filled = 1;
      this.authService.setUserDetails({
        type: this.userDetails.type,
        token: this.userDetails.token,
        user: [this.userDetails.user]
      });
      this.updatePrivacy();
      return;
    }
    this.tourService.initialize(tourItems);
    this.tourService.start();
    this.tourService.end$.subscribe(() => {
      this.userDetails.user.profile_filled = 1;
      this.authService.setUserDetails({
        type: this.userDetails.type,
        token: this.userDetails.token,
        user: [this.userDetails.user]
      });
      this.updatePrivacy();
    });
  }

  getHeaderMenu() {
    let navItemsServer:any[] = [], headerItems:any[] = [];
    
    let getChildMenu = (parentMenu:any, menu_level:number) => {
      for (const mKey in this.menu) {
        let menuItem = this.menu[mKey];
        
        if(menuItem['menu_level'] == (menu_level+1)
        && menuItem['parent_menu'] == parentMenu['menu_id']) {
          let parent = {
            menu_level: menuItem['menu_level'],
            menu_id: menuItem['menu_id'],
            name: (menuItem['display_name'] && menuItem['display_name'].length > 0) ? menuItem['display_name'] : menuItem['menu_name'],
            url: menuItem['menu_link'],
            type: menuItem['link_type'],
            children: [],
            content: menuItem['menu_desc']
          };
          if(menuItem['link_type'] == 1) {
            parent['attributes'] = { target: '_blank', rel: 'noopener' };
          }
          
          let newParent = getChildMenu(parent, parseInt(menuItem['menu_level']));
          if(newParent['children'].length === 0) delete newParent['children'];
          parentMenu['children'].push(newParent);
        }
      }
      
      return parentMenu;
    };
    for (const mKey in this.menu) {
      let menuItem = this.menu[mKey];
      if(menuItem['menu_level'] == 0) {
        let parentMenu = {
          menu_level: menuItem['menu_level'],
          menu_id: menuItem['menu_id'],
          name: (menuItem['display_name'] && menuItem['display_name'].length > 0) ? menuItem['display_name'] : menuItem['menu_name'],
          url: menuItem['menu_link'],
          type: menuItem['link_type'],
          children: [],
          content: menuItem['menu_desc']
        };
        if(menuItem['link_type'] == 1) {
          parentMenu['attributes'] = { target: '_blank', rel: 'noopener' };
        }
    
        let newParentMenu = getChildMenu(parentMenu, parseInt(menuItem['menu_level']));
        if(newParentMenu['children'].length === 0) delete newParentMenu['children'];
    
        // If first header menu and has children
        if(parseInt(mKey) === 0 && newParentMenu['children'] && newParentMenu['children'].length > 0) {
          // Push children to side nav
          navItemsServer.push(newParentMenu);
        }
    
        // Push Patent Menus in Header
        headerItems.push(newParentMenu);
      }
    }

    this.headerItems = headerItems;
  }

  // ── A2.1 — server-driven menu engine consume ──────────────────────────────
  // Fetches GET /api/menu (surface=main) and converts the resolver tree into the
  // exact node shape the existing header/sidebar already render. Legacy untouched.
  async loadServerMenu() {
    // one call → all surfaces: { menu: { main, avatar, mobile } }
    const res:any = await this.menuService.getMenuTree();
    if (!res || res.status !== 1 || !res.menu) return;
    const menu = res.menu;

    const mainTree = Array.isArray(menu) ? menu : (menu.main || []);
    if (mainTree.length) {
      const c1 = { n: 1 };
      this.headerItems = mainTree.map((n:any) => this.convertServerNode(n, 0, c1));
      // Drive the LEFT sidebar from the server tree too (header alone isn't enough —
      // the sidebar is the primary nav). Pick the header matching the current route.
      this.applyServerSidebar();
      // Re-baseline the horizontal nav pager after the new <li>s render (deferred so the
      // *ngFor has painted) — otherwise overflow/arrow state is measured on a stale strip.
      setTimeout(() => this.initNav(), 0);
    }

    const avatarTree = Array.isArray(menu) ? [] : (menu.avatar || []);
    const c2 = { n: 1 };
    this.avatarItems = avatarTree.map((n:any) => this.convertServerNode(n, 0, c2));

    // P4 — on a FRESH login only, open the org/role landing page (set in /user/module).
    // The flag is set by AuthService right after login; consumed once here so plain
    // refreshes / in-app navigation don't yank the user back to the landing page.
    if (localStorage.getItem('engineLandingPending') === '1') {
      localStorage.removeItem('engineLandingPending');
      if (res.landing) {
        try {
          await this.router.navigateByUrl(res.landing);
          this.applyServerSidebar();   // sidebar now reflects the landing page's section
        } catch (e) { /* ignore */ }
      }
    }
  }

  // On initial load (engine ON), select the active top-level header by current URL
  // (fallback: first header with children) and expose its children as the sidebar.
  // Mirrors getChildMenu but without navigating away from the current page.
  applyServerSidebar() {
    if (!this.headerItems || !this.headerItems.length) return;
    const path = (this.router.url || '').replace(/^\/?user\//, '').split('?')[0].split('#')[0];
    const matches = (node:any): boolean =>
      (!!node.url && path.indexOf(node.url) === 0) ||
      (node.children || []).some((c:any) => matches(c));
    let active = this.headerItems.find((h:any) => matches(h));
    if (!active) active = this.headerItems.find((h:any) => h.children && h.children.length) || this.headerItems[0];
    this.currentHead = active;
    this.navItems = (active && active.children) ? active.children : [];
    localStorage.setItem('currentHead', JSON.stringify(this.currentHead));
    localStorage.setItem('currentSides', JSON.stringify(this.navItems));
  }

  // Map { key,label,route,type,icon,module,children } → { menu_id,name,url,type,children,content,attributes }
  convertServerNode(node:any, level:number, counter:{n:number}):any {
    const id = counter.n++;
    const route = node.route || '';
    const isExternal = node.type === 'external' || /^https?:\/\//i.test(route);

    let url = route;
    if (!isExternal && url.indexOf('/user/') === 0) url = url.substring(6); // template prepends 'user/'

    // A group/dropdown has no real route. CoreUI v2 opens a dropdown when
    // router.isActive(item.url, false) is true — and an EMPTY url parses to '/', which
    // is contained in every route → every group would open by default. Give groups a
    // non-routable placeholder so isActive() is false; the active branch still opens via
    // CoreUI's native routerLinkActive="open" on the dropdown container.
    const hasChildren = !!(node.children && node.children.length);
    if (hasChildren && !url) url = 'menu-group/' + (node.key || id);

    const out:any = {
      menu_level: level,
      menu_id: id,
      key: node.key,
      icon: node.icon || null,
      name: node.label,
      url: url,
      type: isExternal ? 1 : 0,   // 1 = external (new tab), matches legacy link_type
      content: ''
    };
    if (isExternal) out.attributes = { target: '_blank', rel: 'noopener' };

    if (node.children && node.children.length) {
      out.children = node.children.map((c:any) => this.convertServerNode(c, level + 1, counter));
    }
    return out;
  }

  async updatePrivacy() {
    let userDetails = await this.authService.getUserDetails(),
    token = userDetails.token ? userDetails.token : '';
    
    // Create header and append JWT token
    let headers = new HttpHeaders({
      'Authorization': `Bearer: ${token}`
    });
    headers.append('Authorization', token);

    return this.http.post(this.apiLink+'/api/'+'usermgmt/update_pf', {}, {headers: headers}).toPromise().then(response => {
      this.response = response;
      if(this.response.status == 0) {
        this.toastr.error(this.response.msg, 'Error!', {
          timeOut: 5000,
        });
        return false;
      }
    });
  }

  // ── Notification bell methods ─────────────────────────────────────────────
  // R37 — the filter / load more / clear methods were lost in commit ef40c613 ("menu bug fix")
  // while the template kept calling them, so every bell button except "Mark all read" threw.
  private get notifUnreadOnly(): boolean { return this.notifFilter === 'unread'; }

  async toggleBell(open: boolean) {
    this.bellOpen = open;
    if (open) await this.reloadNotif();
  }

  private async reloadNotif() {
    this.bellLoading = true;
    this.notifItems = await this.notifFeed.list(this.notifPageSize, 0, this.notifUnreadOnly);
    this.notifCanLoadMore = this.notifItems.length === this.notifPageSize;
    this.bellLoading = false;
    this.notifFeed.refreshUnread();
  }

  async setNotifFilter(f: 'all' | 'unread') {
    if (!f || f === this.notifFilter) return;
    this.notifFilter = f;
    await this.reloadNotif();
  }

  async loadMoreNotif() {
    this.notifLoadingMore = true;
    const next = await this.notifFeed.list(this.notifPageSize, this.notifItems.length, this.notifUnreadOnly);
    this.notifItems = [...this.notifItems, ...next];
    this.notifCanLoadMore = next.length === this.notifPageSize;
    this.notifLoadingMore = false;
  }

  async markAllNotifRead() {
    await this.notifFeed.markAllRead();
    if (this.notifUnreadOnly) this.notifItems = [];
    else this.notifItems = this.notifItems.map(i => ({ ...i, is_read: true }));
  }

  async clearNotif(readOnly: boolean) {
    if (!readOnly && !window.confirm(
      'Clear ALL notifications? This permanently deletes every notification, including unread ones.')) {
      return;
    }
    const ok = await this.notifFeed.clear(readOnly);
    if (!ok) return;
    if (readOnly) this.notifItems = this.notifItems.filter(i => !i.is_read);
    else this.notifItems = [];
    this.notifCanLoadMore = false;
    this.notifFeed.refreshUnread();
  }

  onNotifClick(n: InboxItem) {
    if (!n.is_read) { n.is_read = true; this.notifFeed.markRead([n.id]); }
    this.bellOpen = false;
    // Where it leads lives in NotificationFeedService — shared with the Notifications page.
    const target = this.notifFeed.linkFor(n);
    if (target) this.router.navigate([target.path], target.query ? { queryParams: target.query } : {});
  }

  notifIcon(n: InboxItem): string { return this.notifFeed.iconFor(n); }

  openAllNotifications() {
    this.bellOpen = false;
    this.router.navigate(['/user/notifications']);
  }

  notifAgo(d: string): string {
    if (!d) return '';
    const t = new Date(d.includes('T') ? d : d.replace(' ', 'T') + 'Z').getTime();
    if (isNaN(t)) return '';
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
    if (diff < 86400) return Math.floor(diff / 3600) + ' hr ago';
    if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
    return new Date(t).toLocaleDateString();
  }

  ngOnDestroy() {
    if (this.notifPoll) clearInterval(this.notifPoll);
    this.socketSercie.disconnect();
  }

  async getUserDetails() {
    this.userDetails = await this.authService.getUserDetails();
    // Check if user is still logged in
    if(this.userDetails.relogin && this.userDetails.relogin == 1) {
      await this.authService.removeUserDetails();
      this.authService.trackAuthStatus();
    } else if(this.userDetails.type != 'user') {
      this.authService.trackAuthStatus();
    }

    this.name = this.userDetails.user.reg_title ? ' '+this.userDetails.user.reg_mname : '';
    this.name += this.userDetails.user.reg_title ? ' '+this.userDetails.user.reg_fname : this.userDetails.user.reg_fname;
    this.name += this.userDetails.user.reg_mname ? ' '+this.userDetails.user.reg_mname : '';
    this.name += this.userDetails.user.reg_lname ? ' '+this.userDetails.user.reg_lname : '';
  }

  getChildMenu(parentMenu:any) {
    if(parentMenu.children && parentMenu.children.length > 0) {
      this.navItems = parentMenu.children;
    } else {
      this.navItems = [];
    }

    // Set current header menu in localStorage
    this.currentHead = parentMenu;
    localStorage.setItem('currentHead', JSON.stringify(parentMenu));
    // Set current side menu in localStorage
    localStorage.setItem('currentSides', JSON.stringify(this.navItems));

    // Drill to the first REAL leaf route — the first sidebar item may itself be a group
    // (dropdown) whose url is a non-routable 'menu-group/…' placeholder; navigating to
    // that 404s. firstLeafUrl skips groups and finds the first navigable page.
    const target = this.navItems.length ? this.firstLeafUrl(this.navItems) : null;
    const parentRoutable = parentMenu.url && String(parentMenu.url).indexOf('menu-group/') !== 0;

    if(target) localStorage.setItem('selectedSide', 'user/' + target);
    else this.authService.urlChangeObservable.next('clearLeftSide');

    // Redirect to proper page
    if(parentMenu.type != 2 && target) this.router.navigate(['user/' + target]);
    else if(parentRoutable) this.router.navigate(['user/' + parentMenu['url']]);
    // else: group header with no leaf route → just open its sidebar, stay on current page

    // call observable next everytime page changes
    this.authService.urlChangeObservable.next(null);
  }

  // Recursively find the first descendant with a real navigable route, skipping group
  // dropdowns (url starts with 'menu-group/') and empty urls. Returns null if none.
  firstLeafUrl(items:any[]): string | null {
    for (const it of (items || [])) {
      if (it.children && it.children.length) {
        const u = this.firstLeafUrl(it.children);
        if (u) return u;
      } else if (it.url && String(it.url).indexOf('menu-group/') !== 0) {
        return it.url;
      }
    }
    return null;
  }
  setSelectedMenu() {
    localStorage.setItem('selectedSide', '');
  }

  toggleMinimize(e) {
    this.sidebarMinimized = e;
  }

  async logout() {
    await this.authService.removeUserDetails();
    this.authService.trackAuthStatus();
  }

  // Page-style horizontal scroll (like the MakeMyTrip date strip): one click slides
  // ~a full viewport (with a small overlap so a couple of items carry over for context),
  // snapping to the start/end. Arrows disable at the respective edge.
  navScroll(direction: 'left' | 'right') {
    if (!this.navContainer) return;
    const ul = this.navContainer.nativeElement;
    const viewport = ul.parentElement ? ul.parentElement.clientWidth : ul.clientWidth;
    const maxOffset = Math.max(0, ul.scrollWidth - viewport);
    if (maxOffset <= 0) { this.updateNavArrows(); return; }

    // edge guard
    if (direction === 'left' && this.navOffset <= 0) return;
    if (direction === 'right' && this.navOffset >= maxOffset - 1) return;

    const step = Math.max(160, viewport - 64); // a page, minus a peek of overlap
    let offset = this.navOffset + (direction === 'right' ? step : -step);
    offset = Math.min(maxOffset, Math.max(0, offset));

    this.navOffset = offset;
    ul.style.transform = `translateX(-${offset}px)`; // .navcustom has a CSS transition → smooth slide
    this.updateNavArrows();
  }

  async finishTour() {}
  
  toogleBlackTheme() {
    if(this.currentTheme == 'black-theme') {
      this.loadStyle('default');
    } else {
      this.loadStyle('black-theme');
    }
  }
  
  ///css changer
  async loadStyle(styleName: string) {
    const head = document.getElementsByTagName('head')[0];

    const existingStyle = document.getElementById('client-theme');
    // Remove the previously added style if it exists
    if (existingStyle) {
      head.removeChild(existingStyle);
    }

    const style = document.createElement('link');
    if(styleName && styleName.length > 0) {
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = 'assets/themes/'+styleName+'.css';
    } else {
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = 'assets/themes/default.css';
    }
    head.appendChild(style);
    
    let data = {
      user_id: this.userDetails.user.id,
      theme_name: styleName
    };
    let chnageTheme = await this.themeService.addTheme(data);
    if(chnageTheme){
      this.toastr.success('Theme Successfully Changed.', 'Success!', {
        timeOut: 3000,
      });
    }
    if(styleName == '' || styleName == 'default'){
      this.toastr.success('Theme has been reset to default.', 'Success!', {
        timeOut: 3000,
      });
      // window.location.reload();
    }
    this.currentTheme = styleName;
  }

  //apply theme
  async chnekUsertheme(){
    let data = {
      user_id: this.userDetails.user.id,
    };
    let getTheme:any;
    try {
      getTheme = await this.themeService.getuserTheme(data);
      const head = document.getElementsByTagName('head')[0];
      
      const existingStyle = document.getElementById('client-theme');
      // Remove the previously added style if it exists
      if (existingStyle) {
        head.removeChild(existingStyle);
      }
      
      const style = document.createElement('link');
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      
      let themeId = '';
      if( getTheme && getTheme.length > 0 && getTheme[0].theme_name && getTheme[0].theme_name.length > 0) {
        style.href = 'assets/themes/'+getTheme[0].theme_name+'.css';
        themeId = getTheme[0].theme_name;
      } else {
        style.href = 'assets/themes/default.css';
        themeId = 'default';
      }
      head.appendChild(style);
      this.currentTheme = themeId;
    } catch(err) {
      console.error(err)
    }
  }

  // Zoom In: Increases font size
  zoomIn() {
    if (this.zoomFactor < 1.5) {
      this.zoomFactor += 0.1;
      this.applyZoom();
    }
  }
  // Zoom Out: Decreases font size
  zoomOut() {
    if (this.zoomFactor > 0.5) {
      this.zoomFactor -= 0.1;
      this.applyZoom();
    }
  }
  // Reset Font Size: Returns to the default font size
  resetFontSize() {
    this.zoomFactor = 1;
    this.applyZoom();
  }
  // Apply the current zoom factor to the document's font size
  private applyZoom() {
    this.renderer.setStyle(document.documentElement, 'font-size', `${this.zoomFactor * 100}%`);
  }
}